import React, { useCallback } from 'react'

import { Header, Icon, Text, Tooltip, VStack } from 'govwell-ui'
import { AIChatSourceBreakdown } from 'types/graphql'

import { Link, routes } from '@redwoodjs/router'

import { AIAssistantSettingsTabKey } from 'src/pages/AIAssistantSettingsPage/AIAssistantSettingsPage'

type SourceColumnProps = {
  icon: React.ComponentProps<typeof Icon>['icon']
  label: string
  count: number
  percentage: number
  iconBgColor: string
  iconColor: string
  emptyAction?: React.ReactNode
  tooltip?: string
}

const SourceColumn = React.memo(
  ({
    icon,
    label,
    count,
    percentage,
    iconBgColor,
    iconColor,
    emptyAction,
    tooltip,
  }: SourceColumnProps) => (
    <div className="flex flex-1 flex-col items-center gap-2 rounded-lg border border-solid border-gray-200 bg-white p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${iconBgColor}`}>
        <Icon icon={icon} className={`text-base ${iconColor}`} />
      </div>
      {/* HStack defaults to width:100%, which breaks centering within the card */}
      <div className="flex items-center justify-center gap-1">
        <Text.BodySmall className="font-medium text-gray-500">{label}</Text.BodySmall>
        {tooltip && (
          <Tooltip content={tooltip} side="bottom">
            <span className="cursor-help">
              <Icon icon="FaInfoCircle" className="text-xs text-gray-400" />
            </span>
          </Tooltip>
        )}
      </div>
      {count > 0 ? (
        <>
          <Text.BodyLarge className="font-bold">{count}</Text.BodyLarge>
          <Text.BodySmall className="text-gray-400">{percentage}% of total</Text.BodySmall>
        </>
      ) : (
        <>
          <Text.BodyLarge className="font-bold text-gray-300">0</Text.BodyLarge>
          {emptyAction ?? <Text.BodySmall className="text-gray-400">No questions</Text.BodySmall>}
        </>
      )}
    </div>
  )
)

type Props = {
  sourceBreakdown: AIChatSourceBreakdown
  totalQuestions: number
}

const SourcesSection = ({ sourceBreakdown, totalQuestions }: Props) => {
  const pct = useCallback(
    (count: number) => (totalQuestions > 0 ? Math.round((count / totalQuestions) * 100) : 0),
    [totalQuestions]
  )

  return (
    <VStack gap={4}>
      <Header title="Question Sources" subtitle="Where community members are asking questions" />
      <div className="grid w-full grid-cols-3 gap-4">
        <SourceColumn
          icon="FaGlobe"
          label="Services Portal"
          count={sourceBreakdown.servicesPortal}
          percentage={pct(sourceBreakdown.servicesPortal)}
          iconBgColor="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <SourceColumn
          icon="FaCode"
          label="Website Widget"
          count={sourceBreakdown.websiteWidget}
          percentage={pct(sourceBreakdown.websiteWidget)}
          iconBgColor="bg-purple-50"
          iconColor="text-purple-600"
          tooltip="The website widget lets community members ask questions directly on your own website (e.g. yourcity.gov) and get instant answers without calling or emailing your office."
          emptyAction={
            <Tooltip
              content="Add the widget to your own website (e.g. yourcity.gov) so community members can get instant answers without calling or emailing your office. Installation is quick and easy."
              side="bottom"
            >
              <Link
                to={`${routes.aiAssistantSettings()}?tab=${AIAssistantSettingsTabKey.Installation}`}
                className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-sm font-medium text-red-700 ring-1 ring-red-200 transition-colors hover:bg-red-100"
              >
                <Icon icon="FaExclamationCircle" className="text-red-500" />
                <Text.BodySmall>Set up widget</Text.BodySmall>
              </Link>
            </Tooltip>
          }
        />
        <SourceColumn
          icon="FaDesktop"
          label="In App"
          count={sourceBreakdown.inApp}
          percentage={pct(sourceBreakdown.inApp)}
          iconBgColor="bg-blue-50"
          iconColor="text-blue-600"
        />
      </div>
    </VStack>
  )
}

export default React.memo(SourcesSection)
